import * as React from 'react';
import { mergeStyles, IStyle } from 'office-ui-fabric-react';
import { ThemeColorPalette } from '@fluidx/office-fluid-types';

export interface ChromeProps {
  /** The first slot to be filled by host */
  primarySpace?: React.ReactNode;

  /** The second slot to be filled by host */
  secondarySpace?: React.ReactNode;

  /** Custom styling to be applied to the outer element */
  style?: IStyle;
}

// Test ids for spaces.
export const chromeTestId = 'chromeTestId';
export const chromePrimarySpaceTestId = 'chromePrimarySpaceTestId';
export const chromeSecondarySpaceTestId = 'chromeSecondarySpaceTestId';

/**
 * Allows parent and sibling elements to give directional focus to the chrome
 */
export type FocusOnChrome = (forwardDirection: boolean) => void;

/**
 * The UX bar above the component being displayed
 */
export const Chrome = React.memo((props: ChromeProps) => {
  const { primarySpace, secondarySpace, style } = props;
  return (
    <div className={getChromeClass(style)} data-testid={chromeTestId}>
      {primarySpace && (
        <div className={primaryRegionClass} data-testid={chromePrimarySpaceTestId}>
          {primarySpace}
        </div>
      )}
      {secondarySpace && (
        <div className={secondaryRegionClass} data-testid={chromeSecondarySpaceTestId}>
          {secondarySpace}
        </div>
      )}
    </div>
  );
});

const getChromeClass = (style?: IStyle): string => {
  return mergeStyles(
    {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '48px', // 20px of content & 14px of padding on top/bottom
      paddingLeft: '16px',
      paddingRight: '16px',
      fontSize: '14px',
      lineHeight: '20px',
      color: ThemeColorPalette.Neutral.neutralSecondary
    },
    style
  );
};

// Used by the left & right regions to layout their content
const regionClass = mergeStyles({
  display: 'flex',
  flexDirection: 'row',
  alignContent: 'center',
  alignItems: 'center'
});

const primaryRegionClass = mergeStyles(regionClass, {
  justifyContent: 'flex-start',
  whiteSpace: 'nowrap',
  overflow: 'hidden'
});

const secondaryRegionClass = mergeStyles(regionClass, {
  justifyContent: 'flex-end'
});

// cspell:ignore uifabric testid
